import { useState, useEffect } from "react";
import { doc, getDoc, updateDoc, setDoc, deleteDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { signOut } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, db, storage, functions } from "../firebase";

const VIBES = [
  { id: "culture_vulture", label: "The Culture Vulture", emoji: "🏛️", keywords: ["museum", "history", "art", "culture", "architecture", "heritage"] },
  { id: "midnight_wanderer", label: "The Midnight Wanderer", emoji: "🌙", keywords: ["nightlife", "bars", "party", "clubs", "rooftop", "cocktails", "night"] },
  { id: "soft_life", label: "The Soft Life Traveler", emoji: "✨", keywords: ["luxury", "spa", "resort", "relaxation", "slow", "comfort", "boutique"] },
  { id: "hidden_gem", label: "The Hidden Gem Hunter", emoji: "💎", keywords: ["local", "hidden", "off the beaten", "hole in the wall", "secret", "authentic", "undiscovered"] },
  { id: "foodie", label: "The Foodie Explorer", emoji: "🍜", keywords: ["food", "eat", "restaurant", "kbbq", "street food", "cuisine", "dining", "taste"] },
  { id: "sunrise_chaser", label: "The Sunrise Chaser", emoji: "🌅", keywords: ["hiking", "nature", "sunrise", "outdoor", "trail", "mountain", "adventure", "camping"] },
  { id: "social_butterfly", label: "The Social Butterfly", emoji: "🦋", keywords: ["social", "meet people", "group", "party", "friends", "community"] },
  { id: "digital_nomad", label: "The Digital Nomad", emoji: "💻", keywords: ["cafe", "cowork", "wifi", "remote", "work", "laptop", "slow travel"] },
  { id: "beach_bum", label: "The Beach Bum", emoji: "🏖️", keywords: ["beach", "ocean", "surf", "swim", "snorkel", "island", "coastal", "water"] },
  { id: "thrill_seeker", label: "The Thrill Seeker", emoji: "🎯", keywords: ["extreme", "skydive", "bungee", "adrenaline", "sport", "climbing", "thrill"] },
];

function validateUsernameFormat(u) {
  if (!u) return null;
  if (u.length < 3) return "Must be at least 3 characters.";
  if (u.length > 20) return "Must be 20 characters or fewer.";
  if (!/^[a-z0-9_]+$/.test(u)) return "Only lowercase letters, numbers, and underscores.";
  return null;
}

function assignVibe(interests, vibeText) {
  const combined = [...(interests || []), vibeText || ""].join(" ").toLowerCase();
  let bestVibe = null;
  let bestScore = 0;
  VIBES.forEach(vibe => {
    const score = vibe.keywords.filter(kw => combined.includes(kw)).length;
    if (score > bestScore) { bestScore = score; bestVibe = vibe; }
  });
  return bestScore > 0 ? bestVibe : VIBES[4];
}

export default function EditProfile({ onBack, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [usernameError, setUsernameError] = useState(null);
  const [originalUsername, setOriginalUsername] = useState("");
  const [customInput, setCustomInput] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [referralCode, setReferralCode] = useState(null);
  const [referralCopied, setReferralCopied] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [form, setForm] = useState({
    name: "", username: "", city: "", bio: "", vibeText: "", birthday: "", pronouns: "",
    travelStyle: "", pace: "", food: "", budget: "",
    interests: [], photoURL: null,
  });

  const travelStyles = ["Adventure", "Relaxation", "Culture", "Foodie", "Party", "Backpacker", "Luxury", "Digital Nomad"];
  const paces = ["Slow & easy", "Balanced", "Fast-paced", "Spontaneous"];
  const foodOptions = ["No restrictions", "Vegetarian", "Vegan", "Halal", "Kosher", "Gluten-free", "Pescatarian"];
  const budgets = ["Budget", "Mid-range", "Luxury", "Flexible"];
  const interestOptions = ["Beaches", "Hiking", "Museums", "Nightlife", "Photography", "Cooking", "Yoga", "Surfing", "History", "Art", "Music", "Sport", "Wildlife", "Architecture", "Local markets"];

  const fetchProfile = async () => {
    if (!auth.currentUser) { setLoading(false); setLoadError(true); return; }
    setLoading(true);
    setLoadError(false);
    try {
      const snap = await getDoc(doc(db, "users", auth.currentUser.uid));
      if (snap.exists()) {
        const data = snap.data();
        setForm({
          name: data.name || "", username: data.username || "",
          city: data.city || "", bio: data.bio || "",
          vibeText: data.vibeText || "", birthday: data.birthday || "", pronouns: data.pronouns || "",
          travelStyle: data.travelStyle || "", pace: data.pace || "",
          food: data.food || "", budget: data.budget || "",
          interests: data.interests || [], photoURL: data.photoURL || null,
        });
        setOriginalUsername(data.username || "");
        if (data.referralCode) setReferralCode(data.referralCode);
        if (data.photoURL) setPhotoPreview(data.photoURL);
      }
    } catch (err) {
      console.error("EditProfile.fetchProfile failed:", err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProfile(); }, []);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const toggleInterest = (interest) => {
    setForm(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const addCustomInterest = () => {
    const val = customInput.trim();
    if (!val) return;
    if (form.interests.includes(val)) { setCustomInput(''); return; }
    const customCount = form.interests.filter(i => !interestOptions.includes(i)).length;
    if (customCount >= 10) { setShowCustomInput(false); setCustomInput(''); return; }
    setForm(prev => ({ ...prev, interests: [...prev.interests, val] }));
    setCustomInput('');
  };

  const handleSave = async () => {
    if (!form.username) { setUsernameError("Username is required."); return; }
    const formatErr = validateUsernameFormat(form.username);
    if (formatErr) { setUsernameError(formatErr); return; }

    if (!auth.currentUser) { setSaveError("You've been signed out. Please sign in again."); return; }
    setSaveError(null);
    setSaving(true);
    try {
      const usernameChanged = form.username !== originalUsername;

      if (form.username && usernameChanged) {
        const snap = await getDoc(doc(db, "usernames", form.username));
        if (snap.exists() && snap.data().uid !== auth.currentUser.uid) {
          setUsernameError("This username is already taken.");
          setSaving(false);
          return;
        }
      }

      let photoURL = form.photoURL;
      if (photoFile) {
        const storageRef = ref(storage, `profilePhotos/${auth.currentUser.uid}`);
        await uploadBytes(storageRef, photoFile);
        photoURL = await getDownloadURL(storageRef);
      }

      const vibe = assignVibe(form.interests, form.vibeText);

      await updateDoc(doc(db, "users", auth.currentUser.uid), {
        ...form,
        photoURL,
        vibe: vibe.id,
        vibeLabel: vibe.label,
        vibeEmoji: vibe.emoji,
      });

      if (usernameChanged) {
        if (form.username) {
          await setDoc(doc(db, "usernames", form.username), { uid: auth.currentUser.uid });
        }
        if (originalUsername) {
          try {
            await deleteDoc(doc(db, "usernames", originalUsername));
          } catch (err) {
            console.error("EditProfile: failed to delete old username doc (stale orphan):", err);
          }
        }
        setOriginalUsername(form.username);
      }

      onSaved({ ...form, photoURL, vibe: vibe.id, vibeLabel: vibe.label, vibeEmoji: vibe.emoji });
    } catch (err) {
      console.error("EditProfile.handleSave failed:", err);
      setSaveError("Failed to save. Please try again.");
    }
    setSaving(false);
  };

  const handleCopyReferral = async () => {
    try {
      await navigator.clipboard.writeText(`https://travelsolis.com/invite/${referralCode}`);
      setReferralCopied(true);
      setTimeout(() => setReferralCopied(false), 2000);
    } catch (err) {
      console.error("[handleCopyReferral]", err);
      setSaveError("Failed to copy referral link. Please try again.");
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE" || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const deleteFn = httpsCallable(functions, "deleteAccount");
      await deleteFn();
      setDeleteSuccess(true);
      setTimeout(async () => {
        try { await signOut(auth); } catch (_) {}
      }, 1500);
    } catch (err) {
      console.error("[deleteAccount]", err);
      setDeleteError(err.message || "Something went wrong. Please try again.");
      setDeleting(false);
    }
  };

  const currentVibe = assignVibe(form.interests, form.vibeText);

  const sections = [
    { id: "basic", label: "Basic info", icon: "👤", desc: "Name, city and bio" },
    { id: "vibe", label: "Your vibe", icon: "✨", desc: "Interests and traveler type" },
    { id: "style", label: "Travel style", icon: "✈️", desc: "How you like to travel" },
    { id: "food", label: "Food & budget", icon: "🍽️", desc: "Dietary needs and budget" },
  ];

  if (loading) return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#FAF6F0',fontFamily:'DM Sans,sans-serif',color:'rgba(26,23,20,0.4)',fontSize:14}}>
      Loading profile...
    </div>
  );

  if (loadError) return (
    <div style={{minHeight:'100vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',background:'#FAF6F0',fontFamily:'DM Sans,sans-serif',gap:12,padding:'0 24px',textAlign:'center'}}>
      <span style={{fontSize:32}}>⚠️</span>
      <span style={{fontSize:15,fontWeight:500,color:'#1A1714'}}>Couldn't load your profile</span>
      <span style={{fontSize:13,color:'rgba(26,23,20,0.5)'}}>Check your connection and try again.</span>
      <button onClick={fetchProfile} style={{marginTop:8,padding:'10px 24px',background:'#F5A623',color:'#1E2D4A',border:'none',borderRadius:100,fontSize:13,fontWeight:500,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>
        Retry
      </button>
      <button onClick={onBack} style={{padding:'8px 20px',background:'none',border:'none',fontSize:13,color:'rgba(26,23,20,0.45)',cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>
        ← Go back
      </button>
    </div>
  );

  const firstName = form.name?.split(" ")[0] || "You";

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        .ep-root { min-height: 100vh; background: #FAF6F0; font-family: 'DM Sans', sans-serif; padding-bottom: 40px; }
        .ep-nav { background: white; border-bottom: 0.5px solid rgba(26,23,20,0.08); padding: 16px 24px; display: flex; align-items: center; justify-content: space-between; position: sticky; top: 0; z-index: 100; }
        .ep-back { font-size: 14px; font-weight: 500; color: rgba(26,23,20,0.55); cursor: pointer; background: none; border: none; font-family: 'DM Sans', sans-serif; }
        .ep-back:hover { color: #F5A623; }
        .ep-nav-title { font-family: 'Playfair Display', serif; font-size: 16px; font-weight: 600; color: #1a1714; }
        .ep-save { font-size: 13px; font-weight: 500; color: white; background: #F5A623; border: none; border-radius: 100px; padding: 7px 16px; cursor: pointer; font-family: 'DM Sans', sans-serif; }
        .ep-save:disabled { opacity: 0.5; cursor: not-allowed; }
        .ep-hero { background: #1E2D4A; padding: 28px 24px; display: flex; flex-direction: column; align-items: center; }
        .ep-avatar-wrap { position: relative; margin-bottom: 12px; }
        .ep-avatar { width: 80px; height: 80px; border-radius: 50%; background: #1E2D4A; display: flex; align-items: center; justify-content: center; font-size: 28px; font-weight: 600; color: white; overflow: hidden; border: 3px solid rgba(255,255,255,0.2); cursor: pointer; }
        .ep-avatar img { width: 100%; height: 100%; object-fit: cover; }
        .ep-avatar-edit { position: absolute; bottom: 0; right: 0; width: 24px; height: 24px; border-radius: 50%; background: #F5A623; border: 2px solid white; display: flex; align-items: center; justify-content: center; font-size: 11px; cursor: pointer; }
        .ep-hero-name { font-family: 'Playfair Display', serif; font-size: 20px; font-weight: 600; color: white; margin-bottom: 4px; }
        .ep-hero-city { font-size: 13px; color: rgba(255,255,255,0.5); margin-bottom: 10px; }
        .ep-vibe-badge { display: flex; align-items: center; gap: 8px; background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); border-radius: 100px; padding: 6px 14px; }
        .ep-vibe-badge span { font-size: 12px; color: rgba(255,255,255,0.8); font-weight: 500; }
        .ep-photo-input { display: none; }
        .ep-content { padding: 20px; }
        .ep-section-btn { background: white; border: 0.5px solid rgba(26,23,20,0.08); border-radius: 14px; padding: 14px 16px; display: flex; align-items: center; gap: 12px; cursor: pointer; width: 100%; font-family: 'DM Sans', sans-serif; text-align: left; margin-bottom: 8px; transition: all 0.15s; }
        .ep-section-btn:hover { border-color: #F5A623; }
        .ep-section-btn.active { border-color: #F5A623; background: #e3eef6; }
        .ep-section-icon { width: 38px; height: 38px; border-radius: 10px; background: #FAF6F0; display: flex; align-items: center; justify-content: center; font-size: 16px; flex-shrink: 0; }
        .ep-section-btn.active .ep-section-icon { background: white; }
        .ep-section-info { flex: 1; }
        .ep-section-label { font-size: 14px; font-weight: 500; color: #1a1714; margin-bottom: 2px; }
        .ep-section-desc { font-size: 12px; color: rgba(26,23,20,0.45); font-weight: 300; }
        .ep-chevron { font-size: 16px; color: rgba(26,23,20,0.3); }
        .ep-panel { background: white; border-radius: 16px; border: 0.5px solid rgba(26,23,20,0.08); padding: 20px; margin-bottom: 12px; animation: fadeUp 0.3s ease; }
        @keyframes fadeUp { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        .ep-field { margin-bottom: 12px; }
        .ep-label { display: block; font-size: 12px; font-weight: 500; color: rgba(26,23,20,0.55); margin-bottom: 5px; }
        .ep-input { width: 100%; padding: 12px 14px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 10px; background: #fafaf8; font-size: 14px; font-family: 'DM Sans', sans-serif; color: #1a1714; outline: none; transition: border-color 0.2s; }
        .ep-input:focus { border-color: #F5A623; background: white; }
        .ep-textarea { width: 100%; padding: 12px 14px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 10px; background: #fafaf8; font-size: 14px; font-family: 'DM Sans', sans-serif; color: #1a1714; outline: none; resize: none; height: 80px; transition: border-color 0.2s; }
        .ep-textarea:focus { border-color: #F5A623; background: white; }
        .ep-options { display: flex; flex-wrap: wrap; gap: 8px; }
        .ep-option { padding: 8px 14px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 100px; background: white; font-size: 13px; color: rgba(26,23,20,0.6); cursor: pointer; font-family: 'DM Sans', sans-serif; transition: all 0.15s; }
        .ep-option.selected { background: #F5A623; color: white; border-color: #F5A623; }
        .ep-interests { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 14px; }
        .ep-interest { padding: 7px 13px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 100px; background: white; font-size: 12px; color: rgba(26,23,20,0.6); cursor: pointer; font-family: 'DM Sans', sans-serif; transition: all 0.15s; }
        .ep-interest.selected { background: #e3eef6; color: #F5A623; border-color: #7BA7C4; }
        .ep-interest-custom { padding: 7px 13px; border: 1.5px solid #7BA7C4; border-radius: 100px; background: #e3eef6; font-size: 12px; color: #F5A623; font-family: 'DM Sans', sans-serif; display: inline-flex; align-items: center; gap: 5px; }
        .ep-interest-custom-remove { background: none; border: none; cursor: pointer; color: #F5A623; font-size: 14px; line-height: 1; padding: 0; opacity: 0.6; font-family: 'DM Sans', sans-serif; }
        .ep-interest-custom-remove:hover { opacity: 1; }
        .ep-interest-add { padding: 7px 13px; border: 1.5px dashed rgba(245,166,35,0.4); border-radius: 100px; background: white; font-size: 12px; color: #F5A623; cursor: pointer; font-family: 'DM Sans', sans-serif; transition: all 0.15s; }
        .ep-interest-add:hover { border-color: #F5A623; background: #e3eef6; }
        .ep-interest-input { padding: 6px 12px; border: 1.5px solid #7BA7C4; border-radius: 100px; background: white; font-size: 12px; font-family: 'DM Sans', sans-serif; color: #1a1714; outline: none; width: 130px; }
        .ep-interest-input:focus { border-color: #F5A623; }
        .ep-interest-confirm { padding: 6px 11px; border: none; border-radius: 100px; background: #F5A623; color: white; font-size: 12px; cursor: pointer; font-family: 'DM Sans', sans-serif; }
        .ep-vibe-preview { background: #1E2D4A; border-radius: 12px; padding: 14px 16px; display: flex; align-items: center; gap: 12px; margin-top: 12px; }
        .ep-vibe-emoji { font-size: 24px; }
        .ep-vibe-label { font-family: 'Playfair Display', serif; font-size: 15px; font-weight: 600; color: white; margin-bottom: 2px; }
        .ep-vibe-sub { font-size: 11px; color: rgba(255,255,255,0.5); }
        .ep-referral-section { background: white; border-radius: 14px; border: 0.5px solid rgba(26,23,20,0.08); padding: 16px; margin-bottom: 8px; }
        .ep-referral-label { font-size: 11px; font-weight: 500; color: rgba(26,23,20,0.45); text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 10px; }
        .ep-referral-row { display: flex; align-items: center; gap: 10px; }
        .ep-referral-link { flex: 1; font-size: 12px; color: rgba(26,23,20,0.6); word-break: break-all; font-family: monospace; background: #FAF6F0; border-radius: 8px; padding: 8px 10px; line-height: 1.4; }
        .ep-referral-copy-btn { padding: 8px 14px; background: #F5A623; color: white; border: none; border-radius: 10px; font-size: 12px; font-weight: 500; font-family: 'DM Sans', sans-serif; cursor: pointer; white-space: nowrap; transition: background 0.2s; flex-shrink: 0; }
        .ep-referral-copy-btn:hover { background: #1E2D4A; }
        .ep-danger-zone { background: white; border: 0.5px solid rgba(201,98,47,0.2); border-radius: 16px; padding: 18px 20px; margin-top: 8px; }
        .ep-danger-title { font-size: 13px; font-weight: 500; color: #c9622f; margin-bottom: 6px; }
        .ep-danger-desc { font-size: 12px; color: rgba(201,98,47,0.7); font-weight: 300; margin-bottom: 14px; line-height: 1.5; }
        .ep-danger-btn { width: 100%; padding: 12px; border: 1.5px solid #c9622f; border-radius: 10px; background: white; font-size: 13px; font-weight: 500; color: #c9622f; cursor: pointer; font-family: 'DM Sans', sans-serif; transition: all 0.2s; }
        .ep-danger-btn:hover { background: #c9622f; color: white; }
        .ep-da-overlay { position: fixed; inset: 0; background: rgba(26,23,20,0.55); z-index: 300; display: flex; align-items: flex-end; justify-content: center; }
        .ep-da-modal { background: white; border-radius: 20px 20px 0 0; padding: 28px 24px 40px; width: 100%; max-width: 600px; animation: slideUp 0.3s ease; overflow-y: auto; max-height: 90vh; }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .ep-da-title { font-family: 'Playfair Display', serif; font-size: 22px; font-weight: 600; color: #1a1714; margin-bottom: 6px; }
        .ep-da-sub { font-size: 14px; color: rgba(26,23,20,0.55); font-weight: 300; margin-bottom: 20px; line-height: 1.6; }
        .ep-da-what { background: #fef0ed; border-radius: 12px; padding: 14px 16px; margin-bottom: 20px; }
        .ep-da-what-title { font-size: 11px; font-weight: 500; color: #712B13; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 10px; }
        .ep-da-what-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 7px; }
        .ep-da-what-list li { font-size: 13px; color: #712B13; font-weight: 300; line-height: 1.5; padding-left: 16px; position: relative; }
        .ep-da-what-list li::before { content: "·"; position: absolute; left: 4px; color: #c9622f; }
        .ep-da-confirm-label { font-size: 13px; font-weight: 500; color: #1a1714; margin-bottom: 8px; }
        .ep-da-confirm-label span { color: #c9622f; font-family: monospace; background: #fef0ed; padding: 2px 6px; border-radius: 4px; }
        .ep-da-input { width: 100%; padding: 13px 14px; border: 1.5px solid rgba(26,23,20,0.15); border-radius: 10px; background: #fafaf8; font-size: 14px; font-family: 'DM Sans', sans-serif; color: #1a1714; outline: none; margin-bottom: 16px; letter-spacing: 0.08em; transition: border-color 0.2s; }
        .ep-da-input:focus { border-color: #c9622f; background: white; }
        .ep-da-input.matched { border-color: #c9622f; background: #fef0ed; }
        .ep-da-error { font-size: 13px; color: #c9622f; background: #fef0ed; border-radius: 8px; padding: 10px 14px; margin-bottom: 14px; line-height: 1.5; }
        .ep-da-confirm-btn { width: 100%; padding: 14px; background: #c9622f; color: white; border: none; border-radius: 12px; font-size: 15px; font-weight: 500; font-family: 'DM Sans', sans-serif; cursor: pointer; transition: background 0.2s; margin-bottom: 10px; }
        .ep-da-confirm-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .ep-da-confirm-btn:not(:disabled):hover { background: #a84d25; }
        .ep-da-cancel-btn { width: 100%; padding: 12px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 12px; background: white; font-size: 14px; font-weight: 500; color: rgba(26,23,20,0.5); cursor: pointer; font-family: 'DM Sans', sans-serif; }
        .ep-da-success { text-align: center; padding: 24px 0; }
        .ep-da-success-check { width: 56px; height: 56px; border-radius: 50%; background: #fef0ed; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; }
        .ep-da-success-title { font-family: 'Playfair Display', serif; font-size: 20px; font-weight: 600; color: #1a1714; margin-bottom: 8px; }
        .ep-da-success-sub { font-size: 14px; color: rgba(26,23,20,0.5); font-weight: 300; }
      `}</style>

      <div className="ep-root">
        <div className="ep-nav">
          <button className="ep-back" onClick={onBack}>← Back</button>
          <span className="ep-nav-title">Edit profile</span>
          <button className="ep-save" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>

        <div className="ep-hero">
          <div className="ep-avatar-wrap">
            <label htmlFor="ep-photo" className="ep-avatar">
              {photoPreview ? <img src={photoPreview} alt="" /> : firstName.charAt(0).toUpperCase()}
            </label>
            <label htmlFor="ep-photo" className="ep-avatar-edit">📷</label>
            <input id="ep-photo" type="file" accept="image/*" className="ep-photo-input" onChange={handlePhotoChange} />
          </div>
          <div className="ep-hero-name">{form.name || "Your name"}</div>
          <div className="ep-hero-city">{form.city ? `📍 ${form.city}` : "Add your city"}</div>
          <div className="ep-vibe-badge">
            <span>{currentVibe.emoji}</span>
            <span>{currentVibe.label}</span>
          </div>
        </div>

        <div className="ep-content">
          {saveError && (
            <p style={{fontSize:13,color:'#c9622f',marginBottom:12}}>{saveError}</p>
          )}
          {sections.map(s => (
            <div key={s.id}>
              <button
                className={`ep-section-btn ${activeSection === s.id ? "active" : ""}`}
                onClick={() => setActiveSection(activeSection === s.id ? null : s.id)}
              >
                <div className="ep-section-icon">{s.icon}</div>
                <div className="ep-section-info">
                  <div className="ep-section-label">{s.label}</div>
                  <div className="ep-section-desc">{s.desc}</div>
                </div>
                <span className="ep-chevron">{activeSection === s.id ? "↑" : "↓"}</span>
              </button>

              {activeSection === "basic" && s.id === "basic" && (
                <div className="ep-panel">
                  <div className="ep-field">
                    <label className="ep-label">Your name</label>
                    <input className="ep-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Jamie Martinez" />
                  </div>
                  <div className="ep-field">
                    <label className="ep-label">Username</label>
                    <input
                      className="ep-input"
                      placeholder="e.g. jamietravels"
                      maxLength={20}
                      value={form.username}
                      onChange={e => {
                        const val = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 20);
                        setForm(f => ({...f, username: val}));
                        setUsernameError(val ? validateUsernameFormat(val) : null);
                      }}
                      style={usernameError ? {borderColor:'#c9622f'} : {}}
                    />
                    {usernameError ? (
                      <p style={{fontSize:11,color:'#c9622f',marginTop:4}}>{usernameError}</p>
                    ) : (
                      <p style={{fontSize:11,color:'rgba(26,23,20,0.4)',marginTop:4}}>3–20 characters, lowercase letters, numbers, underscores.</p>
                    )}
                  </div>
                  <div className="ep-field">
                    <label className="ep-label">Home city</label>
                    <input className="ep-input" value={form.city} onChange={e => setForm({...form, city: e.target.value})} placeholder="Los Angeles, CA" />
                  </div>
                  <div className="ep-field">
                    <label className="ep-label">Bio</label>
                    <textarea className="ep-textarea" value={form.bio} onChange={e => setForm({...form, bio: e.target.value})} placeholder="Tell fellow travelers about yourself..." />
                  </div>
                  <div className="ep-field">
                    <label className="ep-label">Birthday <span style={{color:'rgba(26,23,20,0.35)',fontWeight:300}}>— optional</span></label>
                    <input className="ep-input" type="date" value={form.birthday} onChange={e => setForm({...form, birthday: e.target.value})} />
                  </div>
                  <div className="ep-field" style={{marginBottom:0}}>
                    <label className="ep-label">Pronouns <span style={{color:'rgba(26,23,20,0.35)',fontWeight:300}}>— optional</span></label>
                    <input className="ep-input" value={form.pronouns} onChange={e => setForm({...form, pronouns: e.target.value})} placeholder="e.g. she/her, he/him, they/them" />
                  </div>
                </div>
              )}

              {activeSection === "vibe" && s.id === "vibe" && (
                <div className="ep-panel">
                  <div className="ep-field">
                    <label className="ep-label">Quick picks</label>
                    <div className="ep-interests">
                      {interestOptions.map(interest => (
                        <button key={interest}
                          className={`ep-interest ${form.interests.includes(interest) ? "selected" : ""}`}
                          onClick={() => toggleInterest(interest)}>
                          {interest}
                        </button>
                      ))}
                      {form.interests.filter(i => !interestOptions.includes(i)).map(interest => (
                        <span key={interest} className="ep-interest-custom">
                          {interest}
                          <button className="ep-interest-custom-remove" onClick={() => toggleInterest(interest)}>×</button>
                        </span>
                      ))}
                      {showCustomInput ? (
                        <>
                          <input
                            className="ep-interest-input"
                            autoFocus
                            placeholder="e.g. Pottery"
                            value={customInput}
                            onChange={e => setCustomInput(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') addCustomInterest();
                              if (e.key === 'Escape') { setShowCustomInput(false); setCustomInput(''); }
                            }}
                            maxLength={30}
                          />
                          <button className="ep-interest-confirm" onClick={addCustomInterest}>✓</button>
                        </>
                      ) : (
                        form.interests.filter(i => !interestOptions.includes(i)).length < 10 && (
                          <button className="ep-interest-add" onClick={() => setShowCustomInput(true)}>+ Add</button>
                        )
                      )}
                    </div>
                  </div>
                  <div className="ep-field">
                    <label className="ep-label">Describe your vibe in your own words</label>
                    <textarea className="ep-textarea"
                      value={form.vibeText}
                      onChange={e => setForm({...form, vibeText: e.target.value})}
                      placeholder="e.g. I love KBBQ, rooftop bars, thrift shopping, sunrise hikes..." />
                  </div>
                  <div className="ep-vibe-preview">
                    <div className="ep-vibe-emoji">{currentVibe.emoji}</div>
                    <div>
                      <div className="ep-vibe-label">{currentVibe.label}</div>
                      <div className="ep-vibe-sub">Your traveler type — updates as you type ✨</div>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === "style" && s.id === "style" && (
                <div className="ep-panel">
                  <div className="ep-field">
                    <label className="ep-label">Travel style</label>
                    <div className="ep-options">
                      {travelStyles.map(st => (
                        <button key={st} className={`ep-option ${form.travelStyle === st ? "selected" : ""}`}
                          onClick={() => setForm({...form, travelStyle: st})}>{st}</button>
                      ))}
                    </div>
                  </div>
                  <div className="ep-field" style={{marginBottom:0}}>
                    <label className="ep-label">Preferred pace</label>
                    <div className="ep-options">
                      {paces.map(p => (
                        <button key={p} className={`ep-option ${form.pace === p ? "selected" : ""}`}
                          onClick={() => setForm({...form, pace: p})}>{p}</button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeSection === "food" && s.id === "food" && (
                <div className="ep-panel">
                  <div className="ep-field">
                    <label className="ep-label">Dietary preference</label>
                    <div className="ep-options">
                      {foodOptions.map(f => (
                        <button key={f} className={`ep-option ${form.food === f ? "selected" : ""}`}
                          onClick={() => setForm({...form, food: f})}>{f}</button>
                      ))}
                    </div>
                  </div>
                  <div className="ep-field" style={{marginBottom:0}}>
                    <label className="ep-label">Budget range</label>
                    <div className="ep-options">
                      {budgets.map(b => (
                        <button key={b} className={`ep-option ${form.budget === b ? "selected" : ""}`}
                          onClick={() => setForm({...form, budget: b})}>{b}</button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          {referralCode && (
            <div className="ep-referral-section">
              <div className="ep-referral-label">Your Referral Link</div>
              <div className="ep-referral-row">
                <span className="ep-referral-link">https://travelsolis.com/invite/{referralCode}</span>
                <button className="ep-referral-copy-btn" onClick={handleCopyReferral}>
                  {referralCopied ? "✓ Copied!" : "Copy Link"}
                </button>
              </div>
            </div>
          )}

          <div className="ep-danger-zone">
            <div className="ep-danger-title">Danger zone</div>
            <p className="ep-danger-desc">Permanently delete your account and all your data. This cannot be undone.</p>
            <button
              className="ep-danger-btn"
              onClick={() => { setDeleteError(null); setDeleteConfirmText(""); setDeleteSuccess(false); setShowDeleteModal(true); }}
            >
              Delete account
            </button>
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <div
          className="ep-da-overlay"
          onClick={e => { if (e.target === e.currentTarget && !deleting && !deleteSuccess) { setShowDeleteModal(false); setDeleteConfirmText(""); } }}
        >
          <div className="ep-da-modal">
            {deleteSuccess ? (
              <div className="ep-da-success">
                <div className="ep-da-success-check">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M5 13l4 4L19 7" stroke="#c9622f" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="ep-da-success-title">Account deleted</div>
                <p className="ep-da-success-sub">Your account and data have been permanently removed. Signing you out…</p>
              </div>
            ) : (
              <>
                <div className="ep-da-title">Delete your account</div>
                <p className="ep-da-sub">This is permanent and cannot be undone. All your data will be erased.</p>
                <div className="ep-da-what">
                  <div className="ep-da-what-title">What gets deleted</div>
                  <ul className="ep-da-what-list">
                    <li>Your profile, photo, and account</li>
                    <li>Your journal entries and scratch map</li>
                    <li>Trips you're the only member of — deleted entirely</li>
                    <li>Trips you created with others — handed off so the group keeps their trip</li>
                    <li>Your name is anonymised in shared expense history so settle-up records stay intact</li>
                    <li>Your local tips remain but are anonymised (your name is removed)</li>
                  </ul>
                </div>
                <p className="ep-da-confirm-label">Type <span>DELETE</span> to confirm</p>
                <input
                  className={`ep-da-input${deleteConfirmText === "DELETE" ? " matched" : ""}`}
                  type="text"
                  placeholder="DELETE"
                  value={deleteConfirmText}
                  onChange={e => setDeleteConfirmText(e.target.value)}
                  disabled={deleting}
                  autoComplete="off"
                  spellCheck={false}
                />
                {deleteError && <p className="ep-da-error">{deleteError}</p>}
                <button
                  className="ep-da-confirm-btn"
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirmText !== "DELETE" || deleting}
                >
                  {deleting ? "Deleting your account…" : "Permanently delete my account"}
                </button>
                <button
                  className="ep-da-cancel-btn"
                  onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(""); setDeleteError(null); }}
                  disabled={deleting}
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}